import DriverViewModal from "./modals/DriverViewModal";
// Status color helper (must be above all usages)
const getStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
    case "inactive":
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    case "maintenance":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
    case "offline":
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    case "on-delivery":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
  }
};
import { useAppSelector } from "@/hooks/redux";
// Vehicle interface and sample data
import FleetService from "@/services/FleetService";
interface Courier {
  assigned_by: number | string;
  available: boolean;
  completedDeliveries: number;
  days?: string[];
  driver?: string;
  driver_id: any;
  driverId: any;
  email: string;
  end_time: any;
  hours?: string;
  id: string;
  name: string;
  notes: string;
  phone: string;
  profilePicture?: File;
  rating: number;
  shiftEnd: any;
  shiftStart: any;
  start_time: any;
  status: "active" | "offline" | "on-delivery";
  subzones?: (number | string)[];
  vehicle?: string;
  vehicle_id: any;
  vehicleId: any;
  zone: string;
  zones?: (number | string)[];
}
interface Driver {
  address: string;
  assigned_vehicle_id?: string;
  email: string;
  full_name?: string;
  id: string;
  licenseExpiry: string;
  licenseNumber: string;
  name: string;
  phone: string;
  profilePicture?: File;
  rating: number;
  status: "active" | "offline" | "on-delivery";
  vehicle:
    | string
    | {
        brand?: string;
        id?: string;
        licensePlate?: string;
        make?: string;
        model?: string;
        plate_number?: string;
      };
}
import { Edit, Eye, MapPin, Package, Plus, Trash2, Users } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";

import CourierService from "../../../services/CourierService";
import AddCourierModal from "./modals/AddCourierModal";
import AddDeliveryModal from "./modals/AddDeliveryModal";
import AddDriverModal from "./modals/AddDriverModal";
import AddVehicleModal from "./modals/AddVehicleModal";
import AddZoneModal from "./modals/AddZoneModal";
import EditCourierModal from "./modals/EditCourierModal";
import EditDeliveryModal from "./modals/EditDeliveryModal";
import EditDriverModal from "./modals/EditDriverModal";
import EditSubzoneModal from "./modals/EditSubZoneModal";
import EditVehicleModal from "./modals/EditVehicleModal";
import EditZoneModal from "./modals/EditZoneModal";
import VehicleViewModal from "./modals/VehicleViewModal";

interface Vehicle {
  assignedDriver: string | { full_name?: string; id?: string; name?: string };
  brand: string;
  color: string;
  fuel_type: string;
  id: string;
  insurance_number: string;
  max_load: string;
  mileage: string;
  model: string;
  plate_number: string;
  registrationDocument?: File;
  status: "active" | "inactive" | "maintenance";
  type: string;
  year: string;
}

const CourierManagement: React.FC = () => {
  // Vehicles state
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [viewDriver, setViewDriver] = useState<Driver | null>(null);
  const [viewVehicle, setViewVehicle] = useState<null | Vehicle>(null);
  const [vehicleStatusFilter, setVehicleStatusFilter] = useState("");
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showEditVehicle, setShowEditVehicle] = useState(false);
  const [editVehicleData, setEditVehicleData] = useState<null | Vehicle>(null);
  const [subzones, setSubzones] = useState<any[]>([]);

  const [showEditSubzone, setShowEditSubzone] = useState(false);
  const [editSubzoneData, setEditSubzoneData] = useState<any>(null);

  // Drivers state
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [driverStatusFilter, setDriverStatusFilter] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [showEditDriver, setShowEditDriver] = useState(false);
  const [editDriverData, setEditDriverData] = useState<Driver | null>(null);

  // Fetch drivers and vehicles from backend on mount
  const { user } = useAppSelector((state) => state.auth);
  React.useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      FleetService.getDrivers(user.id),
      FleetService.getVehicles(user.id),
      FleetService.getMaintenances(user.id),
    ])
      .then(([driversRes, vehiclesRes, maintenancesRes]) => {
        // Map backend driver objects to local Driver type
        const mappedDrivers = Array.isArray(driversRes)
          ? driversRes.map((d: any) => ({
              address: d.address || "",
              email: d.email || "",
              id: d.id?.toString() || "",
              licenseExpiry: d.licenseExpiry || d.license_expiry || "",
              licenseNumber: d.licenseNumber || d.license_number || "",
              name: d.full_name || d.name || d.driver_name || "",
              phone: d.phone || "",
              profilePicture: undefined,
              rating: d.rating || 0,
              status: d.status || "active",
              vehicle: d.vehicle || d.assigned_vehicle_id || "",
            }))
          : [];
        setDrivers(mappedDrivers);
        // Map backend vehicle objects to local Vehicle type
        const mappedVehicles = Array.isArray(vehiclesRes)
          ? vehiclesRes
              .map((v: any) => ({
                assignedDriver:
                  v.assignedDriver || v.assigned_driver || v.driverId || "",
                brand: v.brand || v.make || "",
                color: v.color || "",
                fuel_type: v.fuel_type || v.fuelType || "",
                id: v.id?.toString() || "",
                insurance_number: v.insurance_number || v.insuranceNumber || "",
                max_load: v.max_load || v.maxLoad || "",
                mileage: v.mileage?.toString() || "0",
                model: v.model || "",
                plate_number: v.plate_number || v.plate || v.licensePlate || "",
                registrationDocument:
                  v.registration_document ||
                  v.registrationDocument ||
                  undefined,
                status: v.status || "active",
                type: v.type || v.vehicleType || "",
                year: v.year || "",
              }))
              .filter((v: any) => v.id && v.id.trim() !== "")
          : [];
        setVehicles(mappedVehicles);
        // Optionally set maintenances if you use them in this component
        // setMaintenances(maintenancesRes); // Uncomment if needed
      })
      .catch((err) => {
        // Optionally handle error state
        console.error(
          "Failed to fetch drivers, vehicles, or maintenances",
          err,
        );
      });
  }, [user?.id]);

  const fetchDrivers = async () => {
    if (!user?.id) return;
    try {
      const driversRes = await FleetService.getDrivers(user.id);
      const mappedDrivers = Array.isArray(driversRes)
        ? driversRes.map((d: any) => ({
            address: d.address || "",
            email: d.email || "",
            id: d.id?.toString() || "",
            licenseExpiry: d.licenseExpiry || d.license_expiry || "",
            licenseNumber: d.licenseNumber || d.license_number || "",
            name: d.full_name || d.name || d.driver_name || "",
            phone: d.phone || "",
            profilePicture: undefined,
            rating: d.rating || 0,
            status: d.status || "active",
            vehicle: d.vehicle || d.assigned_vehicle_id || "",
          }))
        : [];
      setDrivers(mappedDrivers);
    } catch (err) {
      console.error("Failed to fetch drivers", err);
    }
  };

  const filteredVehicles: Vehicle[] = vehicles
    .filter((v) => !vehicleStatusFilter || v.status === vehicleStatusFilter)
    .filter((v) => {
      if (!vehicleSearch) return true;
      const search = vehicleSearch.toLowerCase();
      const model = v.model ? v.model.toLowerCase() : "";
      const plate = v.plate_number ? v.plate_number.toLowerCase() : "";
      let assigned = "";
      if (typeof v.assignedDriver === "string") {
        assigned = v.assignedDriver ? v.assignedDriver.toLowerCase() : "";
      } else if (v.assignedDriver) {
        assigned = (
          v.assignedDriver.full_name ||
          v.assignedDriver.name ||
          v.assignedDriver.id ||
          ""
        ).toLowerCase();
      }
      return (
        model.includes(search) ||
        plate.includes(search) ||
        assigned.includes(search)
      );
    });

  const filteredDrivers: Driver[] = drivers
    .filter((d) => !driverStatusFilter || d.status === driverStatusFilter)
    .filter((d) => {
      if (!driverSearch) return true;
      const search = driverSearch.toLowerCase();
      const name = d.name ? d.name.toLowerCase() : "";
      const email = d.email ? d.email.toLowerCase() : "";
      const phone = d.phone ? d.phone.toLowerCase() : "";
      return (
        name.includes(search) ||
        email.includes(search) ||
        phone.includes(search)
      );
    });
  // Sample data for other tabs
  const [zones, setZones] = useState<any[]>([]);
  const [loadingZones, setLoadingZones] = useState(false);

  const fetchSubzones = async () => {
    try {
      const res = await CourierService.getServiceSubzones(user?.id || "");
      setSubzones(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.error("Failed to fetch subzones");
    }
  };
  useEffect(() => {
    fetchSubzones();
  }, [user?.id]);

  const fetchZones = async () => {
    if (!user?.id) return;
    setLoadingZones(true);
    try {
      const res = await CourierService.getServiceZones(user.id);
      setZones(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.error("Failed to fetch zones");
      console.error("Zone fetch error:", err);
      if (err && typeof err === "object" && "response" in err) {
        console.error("Response data:", (err as any).response.data);
        console.error("Status:", (err as any).response.status);
        console.error("Headers:", (err as any).response.headers);
      }
    } finally {
      setLoadingZones(false);
    }
  };

  useEffect(() => {
    fetchZones();
  }, [user?.id]);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);

  const fetchDeliveries = async () => {
    if (!user?.id) return;
    setLoadingDeliveries(true);
    try {
      const res = await CourierService.getDeliveries(user.id);
      console.debug("[CourierManagement] fetched deliveries:", res);
      setDeliveries(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.error("Failed to fetch deliveries");
      console.error("Delivery fetch error:", err);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  useEffect(() => {
    fetchDeliveries();
  }, [user?.id]);

  const [loadingVehicles, setLoadingVehicles] = useState(false);

  const fetchVehicles = async () => {
    if (!user?.id) return;
    setLoadingVehicles(true);
    try {
      const vehiclesRes = await FleetService.getVehicles(user.id);
      const mappedVehicles = Array.isArray(vehiclesRes)
        ? vehiclesRes.map((v: any) => ({
            assignedDriver:
              v.assignedDriver || v.assigned_driver_id || v.driverId || "",
            brand: v.brand || v.make || "",
            color: v.color || "",
            fuel_type: v.fuel_type || v.fuelType || "",
            id: v.id?.toString() || "",
            insurance_number: v.insurance_number || v.insuranceNumber || "",
            max_load: v.max_load || v.maxLoad || "",
            mileage: v.mileage?.toString() || "0",
            model: v.model || "",
            plate_number: v.plate_number || v.plate || v.licensePlate || "",
            registrationDocument: undefined,
            status: v.status || "active",
            type: v.type || v.vehicleType || "",
            year: v.year || "",
          }))
        : [];
      setVehicles(mappedVehicles);
    } catch (err) {
      toast.error("Failed to fetch vehicles");
      console.error("Failed to fetch vehicles", err);
    } finally {
      setLoadingVehicles(false);
    }
  };
  const parcelTypes = ["Document", "Package", "Fragile", "Electronic"];
  const deliverySpeeds = ["Standard", "Express", "Same Day", "Next Day"];

  // Type definitions
  type Zone = (typeof zones)[number];
  type Delivery = (typeof deliveries)[number];

  // State declarations
  const [activeTab, setActiveTab] = useState("couriers");
  const location = useLocation();

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const tab = params.get("tab");
      if (tab) setActiveTab(tab);
    } catch (err) {
      // ignore
    }
  }, [location.search]);
  const [viewType, setViewType] = useState<{ [key: string]: "card" | "table" }>(
    { couriers: "card", deliveries: "card", zones: "card" },
  );
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [loadingCouriers, setLoadingCouriers] = useState(false);

  const fetchCouriers = async () => {
    if (!user?.id) return;
    setLoadingCouriers(true);
    try {
      const res = await CourierService.getDriverAssignments(user.id);
      // If API returns { success, data: [...] }
      const assignments = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      // Map driver fields into courier object for table rendering
      const parseIds = (val: any): (number | string)[] => {
        if (Array.isArray(val)) return val;
        if (typeof val === "string") {
          try {
            return JSON.parse(val);
          } catch {
            return [];
          }
        }
        return [];
      };
      setCouriers(
        assignments.map((a: any) => ({
          ...a,
          completedDeliveries: a.completedDeliveries ?? 0,
          email: a.driver?.email || "",
          id: a.id,
          name: a.driver?.full_name || "",
          phone: a.driver?.phone || "",
          rating: a.driver?.rating ?? 0,
          status: a.driver?.status || "active",
          subzones: parseIds(a.subzones),
          transport: a.assigned_vehicle_id || "",
          zones: parseIds(a.zones),
        })),
      );
    } catch (err) {
      console.error("Error fetching assigned drivers:", err);
      // Only show error if no data was loaded
      if (couriers.length === 0) {
        toast.error("Failed to fetch assigned drivers");
      }
    } finally {
      setLoadingCouriers(false);
    }
  };

  useEffect(() => {
    fetchCouriers();
  }, [user?.id]);

  // Zones state
  const [zoneCoverageFilter, setZoneCoverageFilter] = useState("");
  const [zoneSearch, setZoneSearch] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState<any>(null);
  // Deliveries state
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState("");
  const [deliverySearch, setDeliverySearch] = useState("");
  const [courierStatusFilter, setCourierStatusFilter] = useState("");
  const [courierZoneFilter, setCourierZoneFilter] = useState("");
  const [courierSearch, setCourierSearch] = useState("");
  // Modal states
  const [showAddCourier, setShowAddCourier] = useState(false);
  const [showEditCourier, setShowEditCourier] = useState(false);
  const [editCourierData, setEditCourierData] = useState<Courier | null>(null);
  const [showAddZone, setShowAddZone] = useState(false);
  const [showEditZone, setShowEditZone] = useState(false);
  const [editZoneData, setEditZoneData] = useState<any>(null);
  const [showAddDelivery, setShowAddDelivery] = useState(false);
  const [showEditDelivery, setShowEditDelivery] = useState(false);
  const [editDeliveryData, setEditDeliveryData] = useState<any>(null);

  // Filtering logic for couriers
  const filteredCouriers: Courier[] = couriers
    .filter((c) => Number(c.assigned_by) === Number(user?.id))
    .filter((c) => !courierStatusFilter || c.status === courierStatusFilter)
    .filter((c) => !courierZoneFilter || c.zone === courierZoneFilter)
    .filter((c) => {
      if (!courierSearch) return true;
      const search = courierSearch.toLowerCase();
      const name = c.name ? c.name.toLowerCase() : "";
      const email = c.email ? c.email.toLowerCase() : "";
      const phone = c.phone ? c.phone.toLowerCase() : "";
      return (
        name.includes(search) ||
        email.includes(search) ||
        phone.includes(search)
      );
    });

  // Filtering logic for zones
  const filteredZones: any[] = zones
    .filter((z) => Number(z.created_by) === Number(user?.id))
    .filter(
      (z) =>
        !zoneCoverageFilter ||
        (z.description || "").includes(zoneCoverageFilter),
    )
    .filter((z) => {
      if (!zoneSearch) return true;
      const search = zoneSearch.toLowerCase();
      const name = z.name ? z.name.toLowerCase() : "";
      const description = z.description ? z.description.toLowerCase() : "";
      return name.includes(search) || description.includes(search);
    });

  // Filtering logic for deliveries
  const filteredDeliveries: Delivery[] = deliveries
    .filter((d) => {
      const ownerIds = [
        d.created_by,
        d.customer_id,
        d.user_id,
        d.sender_id,
        d.created_by_id,
      ];
      return ownerIds.some((id) => Number(id) === Number(user?.id));
    })
    .filter((d) => !deliveryStatusFilter || d.status === deliveryStatusFilter)
    .filter((d) => {
      if (!deliverySearch) return true;
      const search = deliverySearch.toLowerCase();
      const courier = d.courier ? d.courier.toLowerCase() : "";
      const address = d.address ? d.address.toLowerCase() : "";
      return courier.includes(search) || address.includes(search);
    });

  const tabs = [
    { icon: Users, id: "couriers", label: "Assigned Drivers" },
    { icon: MapPin, id: "zones", label: "Service Zones" },
    { icon: Package, id: "deliveries", label: "Deliveries" },
    { icon: Users, id: "drivers", label: "Drivers" },
    { icon: Package, id: "vehicles", label: "Vehicles" },
  ];

  return (
    <div className="w-full space-y-3 sm:space-y-4">
      {/* Tabs */}
      <div className="mb-3 border-b border-gray-200 dark:border-gray-700 sm:mb-4">
        <nav className="-mb-px flex space-x-4 overflow-x-auto sm:space-x-8">
          {tabs.map((tab) => (
            <button
              className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-1 py-2 text-xs font-medium sm:gap-2 sm:text-sm ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      {activeTab === "drivers" && (
        <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-900">
          {/* Add/Edit Driver Modals */}
          <AddDriverModal
            onClose={() => setShowAddDriver(false)}
            onSubmit={async (data) => {
              // Map modal form data to Driver type
              // Map form status to Driver status
              let driverStatus: "active" | "offline" | "on-delivery" = "active";
              if (data.status === "inactive" || data.status === "suspended") {
                driverStatus = "offline";
              } else {
                driverStatus = "active";
              }

              const mapped: Driver = {
                address: data.address,
                email: data.email,
                id: `D${drivers.length + 1}`,
                licenseExpiry: data.licenseExpiry,
                licenseNumber: data.licenseNumber,
                name: data.full_name,
                phone: data.phone,
                profilePicture: undefined,
                rating: 0,
                status: driverStatus,
                vehicle: data.vehicle || "",
              };
              setDrivers((prev) => [...prev, mapped]);
              setShowAddDriver(false);
            }}
            open={showAddDriver}
          />
          {showEditDriver && editDriverData && (
            <EditDriverModal
              initialData={{
                address: editDriverData.address,
                email: editDriverData.email,
                id: editDriverData.id,
                licenseExpiry: editDriverData.licenseExpiry,
                licenseNumber: editDriverData.licenseNumber,
                name: editDriverData.name,
                phone: editDriverData.phone,
                rating: editDriverData.rating || 0,
                status:
                  editDriverData.status === "offline"
                    ? "inactive"
                    : editDriverData.status === "on-delivery"
                      ? "active"
                      : (editDriverData.status as "active" | "inactive" | "suspended"),
                vehicle: editDriverData.assigned_vehicle_id
                  ? String(editDriverData.assigned_vehicle_id)
                  : typeof editDriverData.vehicle === "object" &&
                      editDriverData.vehicle !== null
                    ? editDriverData.vehicle.id || ""
                    : editDriverData.vehicle || "",
              }}
              onClose={() => setShowEditDriver(false)}
              onSubmit={async (data) => {
                // Map form status to Driver status
                let driverStatus: "active" | "offline" | "on-delivery" =
                  "active";
                if (data.status === "inactive") {
                  driverStatus = "offline";
                } else if (data.status === "suspended") {
                  driverStatus = "offline";
                } else {
                  driverStatus = "active";
                }

                const mapped: Driver = {
                  ...editDriverData,
                  address: data.address,
                  email: data.email,
                  licenseExpiry: data.licenseExpiry,
                  licenseNumber: data.licenseNumber,
                  name: data.name,
                  phone: data.phone,
                  status: driverStatus,
                  vehicle: data.vehicle || "",
                };
                setDrivers((prev) =>
                  prev.map((d) => (d.id === editDriverData.id ? mapped : d)),
                );
                setShowEditDriver(false);
              }}
              open={showEditDriver}
            />
          )}
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Drivers ({drivers.length})
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                View:
              </span>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${viewType["drivers"] === "card" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() =>
                  setViewType((prev) => ({ ...prev, drivers: "card" }))
                }
              >
                Grid
              </button>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${viewType["drivers"] === "table" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() =>
                  setViewType((prev) => ({ ...prev, drivers: "table" }))
                }
              >
                Table
              </button>
              <button
                className="ml-2 flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 sm:ml-4 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
                onClick={() => setShowAddDriver(true)}
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Add Driver</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>
          {/* Filters, Search & Export */}
          <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <select
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:w-auto sm:text-sm"
              onChange={(e) => setDriverStatusFilter(e.target.value)}
              value={driverStatusFilter}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
            <input
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:flex-1 sm:text-sm"
              onChange={(e) => setDriverSearch(e.target.value)}
              placeholder="Search drivers..."
              type="text"
              value={driverSearch}
            />
            <button
              className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700 sm:text-sm"
              onClick={() => {
                const csv = [
                  [
                    "ID",
                    "Name",
                    "Email",
                    "Phone",
                    "Vehicle",
                    "Rating",
                    "Status",
                  ],
                  ...filteredDrivers.map((d: Driver) => [
                    d.id,
                    d.name,
                    d.email,
                    d.phone,
                    d.vehicle,
                    d.rating,
                    d.status,
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "drivers.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>
          {/* Card/Table view */}
          {viewType["drivers"] === "card" ? (
            <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredDrivers.map((driver: Driver) => (
                <div
                  className="w-full min-w-0 overflow-hidden rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                  key={driver.id}
                  style={{ wordBreak: "break-word" }}
                >
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h4 className="break-words font-medium text-gray-900 dark:text-gray-100">
                        {driver.name}
                      </h4>
                      <p className="break-words text-sm text-gray-600 dark:text-gray-400">
                        {driver.email}
                      </p>
                      <p className="break-words text-sm text-gray-600 dark:text-gray-400">
                        {driver.phone}
                      </p>
                      <p className="break-words text-sm text-gray-600 dark:text-gray-400">
                        Vehicle:{" "}
                        {typeof driver.vehicle === "object" &&
                        driver.vehicle !== null
                          ? driver.vehicle.plate_number ||
                            driver.vehicle.model ||
                            driver.vehicle.id
                          : driver.vehicle}
                      </p>
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(driver.status)}`}
                    >
                      {driver.status}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">
                          Rating:{" "}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {driver.rating}★
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        className="rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          try {
                            await CourierService.updateDriverStatus(
                              driver.id,
                              newStatus,
                            );
                            toast.success(
                              "Driver status updated successfully!",
                            );
                            await fetchDrivers();
                          } catch (err) {
                            toast.error("Failed to update driver status");
                            console.error("Update driver status error:", err);
                          }
                        }}
                        value={driver.status}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                      </select>
                      <button
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                        onClick={() => setViewDriver(driver)}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                        onClick={() => {
                          setEditDriverData(driver);
                          setShowEditDriver(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {/* Delete button removed */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs dark:divide-gray-700 sm:text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Phone
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Vehicle
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Rating
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                  {filteredDrivers.map((driver: Driver) => (
                    <tr key={driver.id}>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {driver.name}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {driver.phone}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {typeof driver.vehicle === "object" &&
                        driver.vehicle !== null
                          ? driver.vehicle.plate_number ||
                            driver.vehicle.model ||
                            driver.vehicle.id
                          : driver.vehicle}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {driver.rating}★
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <select
                          className="rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            try {
                              await CourierService.updateDriverStatus(
                                driver.id,
                                newStatus,
                              );
                              toast.success(
                                "Driver status updated successfully!",
                              );
                              await fetchDrivers();
                            } catch (err) {
                              toast.error("Failed to update driver status");
                              console.error("Update driver status error:", err);
                            }
                          }}
                          value={driver.status}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="suspended">Suspended</option>
                        </select>
                        <span
                          className={`ml-2 rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(driver.status)}`}
                        >
                          {driver.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <div className="flex gap-2">
                          <button
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                            onClick={() => setViewDriver(driver)}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                            onClick={() => {
                              setEditDriverData(driver);
                              setShowEditDriver(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {viewDriver && (
                            <DriverViewModal
                              driver={{
                                ...viewDriver,
                                assigned_vehicle_id:
                                  viewDriver.assigned_vehicle_id ??
                                  (typeof viewDriver.vehicle === "object"
                                    ? viewDriver.vehicle.id || ""
                                    : viewDriver.vehicle || ""),
                                full_name:
                                  viewDriver.full_name ?? viewDriver.name,
                                status:
                                  viewDriver.status === "active"
                                    ? "active"
                                    : viewDriver.status === "offline"
                                      ? "inactive"
                                      : viewDriver.status === "on-delivery"
                                        ? "active"
                                        : "active",
                                vehicle:
                                  typeof viewDriver.vehicle === "object" &&
                                  viewDriver.vehicle !== null
                                    ? viewDriver.vehicle
                                    : typeof viewDriver.vehicle === "string"
                                      ? vehicles.find(
                                          (v) => v.id === viewDriver.vehicle,
                                        ) || viewDriver.vehicle
                                      : "",
                              }}
                              onClose={() => setViewDriver(null)}
                              vehicles={vehicles}
                            />
                          )}
                          <button className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600">
                            {/* Delete button removed */}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {activeTab === "vehicles" && (
        <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-900">
          {/* Add/Edit Vehicle Modals */}
          <AddVehicleModal
            onClose={() => setShowAddVehicle(false)}
            onSubmit={async (data) => {
              // Map modal form data to Vehicle type
              const mapped: Vehicle = {
                assignedDriver: data.assignedDriver,
                brand: data.brand,
                color: data.color,
                fuel_type: data.fuel_type,
                id: `V${vehicles.length + 1}`,
                insurance_number: data.insurance_number,
                max_load: data.max_load,
                mileage: data.mileage || "0",
                model: data.model,
                plate_number: data.plate_number,
                registrationDocument: data.registrationDocument,
                status: data.status === "retired" ? "inactive" : data.status,
                type: data.type,
                year: data.year,
              };
              setVehicles((prev) => [...prev, mapped]);
              setShowAddVehicle(false);
            }}
            open={showAddVehicle}
          />
          {showEditVehicle && editVehicleData && (
            <EditVehicleModal
              drivers={drivers}
              initialData={{
                assignedDriver:
                  typeof editVehicleData.assignedDriver === "string"
                    ? editVehicleData.assignedDriver
                    : editVehicleData.assignedDriver?.id ||
                      editVehicleData.assignedDriver?.name ||
                      editVehicleData.assignedDriver?.full_name ||
                      "",
                brand: editVehicleData.brand,
                color: editVehicleData.color,
                fuel_type: editVehicleData.fuel_type,
                id: editVehicleData.id,
                insurance_number: editVehicleData.insurance_number
                  ? String(editVehicleData.insurance_number)
                  : "",
                max_load: editVehicleData.max_load
                  ? String(editVehicleData.max_load)
                  : "",
                mileage: editVehicleData.mileage
                  ? String(parseInt(editVehicleData.mileage, 10))
                  : "0",
                model: editVehicleData.model,
                plate_number: editVehicleData.plate_number,
                registrationDocument: editVehicleData.registrationDocument,
                status: editVehicleData.status,
                type: editVehicleData.type,
                year: editVehicleData.year,
              }}
              onClose={() => setShowEditVehicle(false)}
              onSubmit={async (data, id) => {
                let payload;
                if (
                  data.registrationDocument &&
                  data.registrationDocument instanceof File
                ) {
                  const fd = new FormData();
                  fd.append("brand", data.brand ?? "");
                  fd.append("id", id ?? "");
                  fd.append("color", data.color ?? "");
                  fd.append("fuel_type", data.fuel_type ?? "");
                  fd.append("insurance_number", data.insurance_number ?? "");
                  fd.append("max_load", data.max_load ?? "");
                  fd.append(
                    "mileage",
                    data.mileage ? String(parseInt(data.mileage, 10)) : "0",
                  );
                  fd.append("model", data.model ?? "");
                  fd.append("plate_number", data.plate_number ?? "");
                  fd.append("status", data.status ?? "active");
                  fd.append("type", data.type ?? "");
                  fd.append("year", data.year ?? "");
                  fd.append(
                    "assigned_driver_id",
                    data.assignedDriver ? String(data.assignedDriver) : "",
                  );
                  fd.append("registration_document", data.registrationDocument);
                  payload = fd;
                } else {
                  payload = {
                    assigned_driver_id:
                      data.assignedDriver !== undefined &&
                      data.assignedDriver !== null &&
                      data.assignedDriver !== "null"
                        ? String(data.assignedDriver)
                        : "",
                    brand: data.brand ? String(data.brand) : "",
                    color: data.color ? String(data.color) : "",
                    fuel_type: data.fuel_type ? String(data.fuel_type) : "",
                    id: id ? String(id) : "",
                    insurance_number: data.insurance_number
                      ? String(data.insurance_number)
                      : "",
                    max_load: data.max_load ? String(data.max_load) : "",
                    mileage: data.mileage
                      ? String(parseInt(data.mileage, 10))
                      : "0",
                    model: data.model ? String(data.model) : "",
                    plate_number: data.plate_number
                      ? String(data.plate_number)
                      : "",
                    status: data.status ? String(data.status) : "active",
                    type: data.type ? String(data.type) : "",
                    year: data.year ? String(data.year) : "",
                  };
                }
                if (!id) {
                  console.error(
                    "[CourierManagement] ERROR: Vehicle id is missing! Update will fail. Data:",
                    data,
                  );
                  toast.error("Vehicle ID is missing. Cannot update vehicle.");
                  return;
                }
                await FleetService.updateVehicle(id, payload);
                setShowEditVehicle(false);
                // Refresh vehicle list from backend
                if (typeof fetchVehicles === "function") {
                  await fetchVehicles();
                }
              }}
              open={showEditVehicle}
            />
          )}
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Vehicles ({vehicles.length})
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                View:
              </span>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${viewType["vehicles"] === "card" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() =>
                  setViewType((prev) => ({ ...prev, vehicles: "card" }))
                }
              >
                Grid
              </button>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${viewType["vehicles"] === "table" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() =>
                  setViewType((prev) => ({ ...prev, vehicles: "table" }))
                }
              >
                Table
              </button>
              <button
                className="ml-2 flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 sm:ml-4 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
                onClick={() => setShowAddVehicle(true)}
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Add Vehicle</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>
          {/* Filters, Search & Export */}
          <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <select
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:w-auto sm:text-sm"
              onChange={(e) => setVehicleStatusFilter(e.target.value)}
              value={vehicleStatusFilter}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="maintenance">Maintenance</option>
              <option value="inactive">Inactive</option>
            </select>
            <input
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:flex-1 sm:text-sm"
              onChange={(e) => setVehicleSearch(e.target.value)}
              placeholder="Search vehicles..."
              type="text"
              value={vehicleSearch}
            />
            <button
              className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700 sm:text-sm"
              onClick={() => {
                const csv = [
                  ["ID", "Type", "Model", "Plate", "Assigned Driver", "Status"],
                  ...filteredVehicles.map((v: Vehicle) => [
                    v.id,
                    v.type,
                    v.model,
                    v.plate_number,
                    v.assignedDriver,
                    v.status,
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "vehicles.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>
          {/* Card/Table view */}
          {viewType["vehicles"] === "card" ? (
            <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredVehicles.map((vehicle: Vehicle) => (
                <div
                  className="w-full min-w-0 overflow-hidden rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                  key={vehicle.id}
                  style={{ wordBreak: "break-word" }}
                >
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h4 className="break-words font-medium text-gray-900 dark:text-gray-100">
                        {vehicle.brand} {vehicle.model}
                      </h4>
                      <p className="break-words text-sm text-gray-600 dark:text-gray-400">
                        Type: {vehicle.type}
                      </p>
                      <p className="break-words text-sm text-gray-600 dark:text-gray-400">
                        Plate: {vehicle.plate_number}
                      </p>
                      <p className="break-words text-sm text-gray-600 dark:text-gray-400">
                        Assigned Driver:{" "}
                        {typeof vehicle.assignedDriver === "object" &&
                        vehicle.assignedDriver !== null
                          ? vehicle.assignedDriver.full_name ||
                            vehicle.assignedDriver.name ||
                            vehicle.assignedDriver.id
                          : vehicle.assignedDriver}
                      </p>
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(vehicle.status)}`}
                    >
                      {vehicle.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      className="rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                      onChange={async (e) => {
                        const newStatus = e.target.value;
                        try {
                          await CourierService.updateVehicleStatus(
                            vehicle.id,
                            newStatus,
                          );
                          toast.success("Vehicle status updated successfully!");
                          await fetchVehicles();
                        } catch (err) {
                          toast.error("Failed to update vehicle status");
                          console.error("Update vehicle status error:", err);
                        }
                      }}
                      value={vehicle.status}
                    >
                      <option value="active">Active</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <button
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                      onClick={() => setViewVehicle(vehicle)}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                      onClick={() => {
                        setEditVehicleData(vehicle);
                        setShowEditVehicle(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600">
                      {/* Delete button removed */}
                    </button>
                  </div>
                  {/* Render VehicleViewModal once at the root of the vehicles tab, not here */}
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs dark:divide-gray-700 sm:text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Plate
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Assigned Driver
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                  {filteredVehicles.map((vehicle: Vehicle) => (
                    <tr key={vehicle.id}>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {vehicle.type}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {vehicle.brand} {vehicle.model}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {vehicle.plate_number}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {typeof vehicle.assignedDriver === "object" &&
                        vehicle.assignedDriver !== null
                          ? vehicle.assignedDriver.full_name ||
                            vehicle.assignedDriver.name ||
                            vehicle.assignedDriver.id
                          : vehicle.assignedDriver}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <select
                          className="rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            try {
                              await CourierService.updateVehicleStatus(
                                vehicle.id,
                                newStatus,
                              );
                              toast.success(
                                "Vehicle status updated successfully!",
                              );
                              await fetchVehicles();
                            } catch (err) {
                              toast.error("Failed to update vehicle status");
                              console.error(
                                "Update vehicle status error:",
                                err,
                              );
                            }
                          }}
                          value={vehicle.status}
                        >
                          <option value="active">Active</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="inactive">Inactive</option>
                        </select>
                        <span
                          className={`ml-2 rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(vehicle.status)}`}
                        >
                          {vehicle.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <div className="flex gap-2">
                          <button
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                            onClick={() => setViewVehicle(vehicle)}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            className="mr-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                            onClick={() => {
                              setEditVehicleData(vehicle);
                              setShowEditVehicle(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600">
                            {/* Delete button removed */}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <VehicleViewModal
            onClose={() => setViewVehicle(null)}
            open={!!viewVehicle}
            vehicle={
              viewVehicle
                ? {
                    ...viewVehicle,
                    registrationDocument:
                      viewVehicle.registrationDocument instanceof File
                        ? viewVehicle.registrationDocument.name
                        : (viewVehicle.registrationDocument ?? undefined),
                  }
                : null
            }
          />
        </div>
      )}

      {/* Tab Content */}
      {activeTab === "couriers" && (
        <>
          <AddCourierModal
            drivers={drivers.filter(
              (d) =>
                !couriers.some(
                  (c) => c.driver_id?.toString() === d.id?.toString(),
                ),
            )}
            onClose={() => setShowAddCourier(false)}
            onSubmit={async (data) => {
              try {
                const payload = {
                  assigned_by: user?.id,
                  available: data.availability,
                  days: data.days,
                  driver_id: data.driverId,
                  notes: data.notes,
                  subzones: data.subzones,
                  vehicle_id: data.vehicleId,
                  zones: data.zones,
                };
                await CourierService.createDriverAssignment(payload);
                toast.success("Driver assignment created successfully");
                setShowAddCourier(false);
                await fetchCouriers();
              } catch (err) {
                toast.error("Failed to create driver assignment");
                console.error("Driver assignment error:", err);
                if (err && typeof err === "object" && "response" in err) {
                  console.error("Response data:", (err as any).response.data);
                  console.error("Status:", (err as any).response.status);
                  console.error("Headers:", (err as any).response.headers);
                }
              }
            }}
            open={showAddCourier}
            subzones={subzones.map((sz) => ({
              id: sz.id,
              name: sz.name,
              zoneId: sz.zone_id || sz.zoneId,
            }))}
            vehicles={vehicles
              .filter((v) => v.status === "active")
              .map((v) => ({
                id: v.id,
                registration: v.plate_number,
                type: v.type,
              }))}
            zones={filteredZones.map((zone) => ({
              id: zone.id,
              name: zone.name,
            }))}
          />
          {showEditCourier && editCourierData && (
            <EditCourierModal
              drivers={drivers.filter(
                (d) =>
                  !couriers.some(
                    (c) =>
                      c.driver_id?.toString() === d.id?.toString() &&
                      c.id?.toString() !== editCourierData.id?.toString(),
                  ),
              )}
              initialData={{
                availability: editCourierData.available ?? true,
                days: Array.isArray(editCourierData.days)
                  ? editCourierData.days
                  : [],
                driverId:
                  editCourierData.driver_id?.toString() ||
                  editCourierData.driverId?.toString() ||
                  editCourierData.id?.toString() ||
                  "",
                notes: editCourierData.notes || "",
                subzones: Array.isArray(editCourierData.subzones)
                  ? editCourierData.subzones.map((s: any) => s?.toString())
                  : [],
                vehicleId:
                  editCourierData.vehicle_id?.toString() ||
                  editCourierData.vehicleId?.toString() ||
                  "",
                zones: Array.isArray(editCourierData.zones)
                  ? editCourierData.zones.map((z: any) => z?.toString())
                  : editCourierData.zone
                    ? [editCourierData.zone]
                    : [],
              }}
              onClose={() => setShowEditCourier(false)}
              onSubmit={async (data) => {
                try {
                  const driverIdNum =
                    typeof data.driverId === "string"
                      ? parseInt(data.driverId, 10)
                      : data.driverId;

                  await CourierService.updateDriverAssignment(
                    editCourierData.id?.toString() || "",
                    {
                      available: data.availability,
                      days: data.days,
                      driver_id: driverIdNum,
                      notes: data.notes,
                      subzones: data.subzones,
                      vehicle_id: data.vehicleId,
                      zones: data.zones,
                    },
                  );
                  toast.success("Driver assignment updated successfully");
                  setShowEditCourier(false);
                  await fetchCouriers();
                } catch (err) {
                  toast.error("Failed to update driver assignment");
                  console.error("Update error:", err);
                  if (err && typeof err === "object" && "response" in err) {
                    const errorWithResponse = err as any;
                    if (errorWithResponse.response?.data?.errors) {
                      console.error(
                        "Validation errors:",
                        errorWithResponse.response.data.errors,
                      );
                    }
                  }
                }
              }}
              open={showEditCourier}
              subzones={subzones.map((sz) => ({
                id: sz.id,
                name: sz.name,
                zoneId: sz.zone_id || sz.zoneId,
              }))}
              vehicles={vehicles
                .filter((v) => v.status === "active")
                .map((v) => ({
                  id: v.id,
                  registration: v.plate_number,
                  type: v.type,
                }))}
              zones={filteredZones.map((zone) => ({
                id: zone.id,
                name: zone.name,
              }))}
            />
          )}
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Assigned Drivers ({filteredCouriers.length})
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                View:
              </span>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${viewType["couriers"] === "card" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() =>
                  setViewType((prev) => ({ ...prev, couriers: "card" }))
                }
              >
                Grid
              </button>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${viewType["couriers"] === "table" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() =>
                  setViewType((prev) => ({ ...prev, couriers: "table" }))
                }
              >
                Table
              </button>
              <button
                className="ml-2 flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 sm:ml-4 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
                onClick={() => setShowAddCourier(true)}
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Assign Driver</span>
                <span className="sm:hidden">Assign</span>
              </button>
            </div>
          </div>
          {/* Filters, Search & Export */}
          <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <select
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:w-auto sm:text-sm"
              onChange={(e) => setCourierStatusFilter(e.target.value)}
              value={courierStatusFilter}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="offline">Offline</option>
              <option value="on-delivery">On Delivery</option>
            </select>
            <select
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:w-auto sm:text-sm"
              onChange={(e) => setCourierZoneFilter(e.target.value)}
              value={courierZoneFilter}
            >
              <option value="">All Zones</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.name}>
                  {zone.name}
                </option>
              ))}
            </select>
            <input
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:flex-1 sm:text-sm"
              onChange={(e) => setCourierSearch(e.target.value)}
              placeholder="Search couriers..."
              type="text"
              value={courierSearch}
            />
            <button
              className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700 sm:text-sm"
              onClick={() => {
                const csv = [
                  [
                    "ID",
                    "Name",
                    "Email",
                    "Phone",
                    "Zone",
                    "Rating",
                    "Status",
                    "Completed Deliveries",
                  ],
                  ...filteredCouriers.map((c: Courier) => [
                    c.id,
                    c.name,
                    c.email,
                    c.phone,
                    c.zone,
                    c.rating,
                    c.status,
                    c.completedDeliveries,
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "couriers.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>
          {/* Filtering logic removed, now handled by filteredCouriers variable */}
          {/* ...existing code for viewType["couriers"] === "card" and table... */}
          {viewType["couriers"] === "card" ? (
            <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredCouriers.map((courier: Courier) => (
                <div
                  className="w-full min-w-0 overflow-hidden rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                  key={courier.id}
                  style={{ wordBreak: "break-word" }}
                >
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h4 className="break-words font-medium text-gray-900 dark:text-gray-100">
                        {courier.name}
                      </h4>
                      <p className="break-words text-sm text-gray-600 dark:text-gray-400">
                        {courier.email}
                      </p>
                      <p className="break-words text-sm text-gray-600 dark:text-gray-400">
                        {courier.phone}
                      </p>
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(courier.status)}`}
                    >
                      {courier.status}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">
                          Zones:{" "}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {courier.zones && courier.zones.length > 0
                            ? courier.zones
                                .map(
                                  (zId) =>
                                    zones.find(
                                      (z) =>
                                        z.id?.toString() === zId?.toString(),
                                    )?.name || zId,
                                )
                                .join(", ")
                            : courier.zone || "—"}
                        </span>
                      </div>
                      {courier.subzones && courier.subzones.length > 0 && (
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">
                            Subzones:{" "}
                          </span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {courier.subzones
                              .map(
                                (szId) =>
                                  subzones.find(
                                    (s) =>
                                      s.id?.toString() === szId?.toString(),
                                  )?.name || szId,
                              )
                              .join(", ")}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">
                          Rating:{" "}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {courier.rating}★
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(courier.status)}`}
                      >
                        {courier.status}
                      </span>
                      <button
                        className={`rounded px-2 py-1 text-xs ${
                          courier.available
                            ? "bg-green-600 text-white hover:bg-green-700"
                            : "bg-gray-400 text-white hover:bg-gray-500"
                        }`}
                        onClick={async () => {
                          try {
                            await CourierService.toggleDriverAvailability(
                              courier.id,
                            );
                            toast.success("Availability toggled successfully!");
                            await fetchCouriers();
                          } catch (err) {
                            toast.error("Failed to toggle availability");
                            console.error("Toggle availability error:", err);
                          }
                        }}
                      >
                        {courier.available ? "Available" : "Unavailable"}
                      </button>
                      <button
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                        onClick={() => {
                          setEditCourierData({
                            ...courier,
                            days: courier.days || [],
                          });
                          setShowEditCourier(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {/* Delete button removed */}
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Completed Deliveries: {courier.completedDeliveries}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs dark:divide-gray-700 sm:text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Phone
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Transport
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Zone
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Rating
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Completed Deliveries
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Availability
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                  {filteredCouriers.map((courier: Courier) => (
                    <tr key={courier.id}>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {courier.name}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {courier.phone}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {(() => {
                          // Try vehicleId/vehicle_id first
                          let vehicleId =
                            courier.vehicleId || courier.vehicle_id;
                          // If not present, try driver's assigned_vehicle_id (only if driver is an object)
                          if (
                            !vehicleId &&
                            courier.driver &&
                            typeof courier.driver === "object" &&
                            "assigned_vehicle_id" in courier.driver
                          ) {
                            vehicleId = (courier.driver as any)
                              .assigned_vehicle_id;
                          }
                          const v = vehicles.find(
                            (veh) =>
                              veh.id?.toString() === vehicleId?.toString(),
                          );
                          return v
                            ? `${v.brand || ""} ${v.model || ""} ${v.plate_number || ""}`.trim()
                            : "";
                        })()}
                      </td>
                      <td className="px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <div>
                          {courier.zones && courier.zones.length > 0
                            ? courier.zones
                                .map(
                                  (zId) =>
                                    zones.find(
                                      (z) =>
                                        z.id?.toString() === zId?.toString(),
                                    )?.name || zId,
                                )
                                .join(", ")
                            : courier.zone || "—"}
                        </div>
                        {courier.subzones && courier.subzones.length > 0 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {courier.subzones
                              .map(
                                (szId) =>
                                  subzones.find(
                                    (s) =>
                                      s.id?.toString() === szId?.toString(),
                                  )?.name || szId,
                              )
                              .join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {courier.rating}★
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {courier.completedDeliveries}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(courier.status)}`}
                        >
                          {courier.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <button
                          className={`rounded px-2 py-1 text-xs ${
                            courier.available
                              ? "bg-green-600 text-white hover:bg-green-700"
                              : "bg-gray-400 text-white hover:bg-gray-500"
                          }`}
                          onClick={async () => {
                            try {
                              await CourierService.toggleDriverAvailability(
                                courier.id,
                              );
                              toast.success(
                                "Availability toggled successfully!",
                              );
                              await fetchCouriers();
                            } catch (err) {
                              toast.error("Failed to toggle availability");
                              console.error("Toggle availability error:", err);
                            }
                          }}
                        >
                          {courier.available ? "Available" : "Unavailable"}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <button
                          className="mr-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                          onClick={() => {
                            setEditCourierData({
                              ...courier,
                              days: courier.days || [],
                            });
                            setShowEditCourier(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600">
                          {/* Delete button removed */}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === "zones" && (
        <>
          <AddZoneModal
            onClose={() => setShowAddZone(false)}
            onSubmit={async (data) => {
              setShowAddZone(false);
              // If data has latitude/longitude, it's a subzone; otherwise, it's a zone
              if (data && ("latitude" in data || "longitude" in data)) {
                toast.success("Subzone added");
                await fetchSubzones();
              } else {
                toast.success("Zone added");
                await fetchZones();
              }
            }}
            open={showAddZone}
          />
          {showEditSubzone && editSubzoneData && (
            <EditSubzoneModal
              initialData={{
                description: editSubzoneData.description,
                latitude: editSubzoneData.latitude,
                longitude: editSubzoneData.longitude,
                name: editSubzoneData.name,
                zone_id: editSubzoneData.zone_id,
              }}
              onClose={() => setShowEditSubzone(false)}
              onSubmit={async (data) => {
                try {
                  await CourierService.updateServiceSubzone(
                    editSubzoneData.zone_id,
                    editSubzoneData.id,
                    data,
                  );
                  toast.success("Subzone updated");
                  setShowEditSubzone(false);
                  await fetchSubzones();
                } catch (err) {
                  toast.error("Failed to update subzone");
                }
              }}
              open={showEditSubzone}
              zones={zones}
            />
          )}
          {showEditZone && editZoneData && (
            <EditZoneModal
              initialData={{
                description: editZoneData.description,
                name: editZoneData.name,
              }}
              onClose={() => setShowEditZone(false)}
              onSubmit={async (data) => {
                try {
                  await CourierService.updateServiceZone(editZoneData.id, data);
                  toast.success("Zone updated");
                  setShowEditZone(false);
                  await fetchZones();
                } catch (err) {
                  toast.error("Failed to update zone");
                }
              }}
              open={showEditZone}
            />
          )}
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Service Zones ({zones.length})
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                View:
              </span>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${viewType["zones"] === "card" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() =>
                  setViewType((prev) => ({ ...prev, zones: "card" }))
                }
              >
                Grid
              </button>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${viewType["zones"] === "table" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() =>
                  setViewType((prev) => ({ ...prev, zones: "table" }))
                }
              >
                Table
              </button>
              <button
                className="ml-2 flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 sm:ml-4 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
                onClick={() => setShowAddZone(true)}
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Add Zone</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>
          {/* Filters, Search & Export */}
          <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <select
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:w-auto sm:text-sm"
              onChange={(e) => setZoneCoverageFilter(e.target.value)}
              value={zoneCoverageFilter}
            >
              <option value="">All Coverage</option>
              {Array.from(new Set(zones.map((z: any) => z.description))).map(
                (desc) => (
                  <option key={desc} value={desc}>
                    {desc}
                  </option>
                ),
              )}
            </select>
            <input
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:flex-1 sm:text-sm"
              onChange={(e) => setZoneSearch(e.target.value)}
              placeholder="Search zones..."
              type="text"
              value={zoneSearch}
            />
            <button
              className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 sm:text-sm"
              onClick={() => {
                const csv = [
                  ["ID", "Name", "Coverage"],
                  ...filteredZones.map((z: Zone) => [
                    z.id,
                    z.name,
                    z.description,
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "zones.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>
          {viewType["zones"] === "card" ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredZones.map((zone: Zone) => (
                <div
                  className="flex flex-col gap-2 rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                  key={zone.id}
                >
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      {zone.name}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Coverage: {zone.description}
                    </p>
                    <div className="mt-2">
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Subzones:
                      </span>
                      <ul className="ml-4 list-disc text-xs text-gray-700 dark:text-gray-300">
                        {subzones.filter((sz) => sz.zone_id == zone.id)
                          .length === 0 ? (
                          <li className="text-gray-400 dark:text-gray-500">
                            No subzones
                          </li>
                        ) : (
                          subzones
                            .filter((sz) => sz.zone_id == zone.id)
                            .map((sz) => (
                              <li
                                className="flex items-center justify-between"
                                key={sz.id}
                              >
                                <span>{sz.name}</span>
                                {/* <button
                                  className="ml-2 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                                  onClick={() => {
                                    setEditSubzoneData(sz);
                                    setShowEditSubzone(true);
                                  }}
                                >
                                  Edit
                                </button> */}
                              </li>
                            ))
                        )}
                      </ul>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    {/* <button
                      className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                      onClick={() => {
                        setEditZoneData(zone);
                        setShowEditZone(true);
                      }}
                    >
                      Edit Zone
                    </button> */}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs dark:divide-gray-700 sm:text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      ID
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Coverage
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Subzones
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                  {filteredZones.map((zone: Zone) => (
                    <tr key={zone.id}>
                      <td className="px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {zone.id}
                      </td>
                      <td className="px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {zone.name}
                      </td>
                      <td className="px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {zone.description}
                      </td>
                      <td className="px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <ul>
                          {subzones.filter((sz) => sz.zone_id == zone.id)
                            .length === 0 ? (
                            <li className="text-gray-400 dark:text-gray-500">
                              No subzones
                            </li>
                          ) : (
                            subzones
                              .filter((sz) => sz.zone_id == zone.id)
                              .map((sz) => (
                                <li
                                  className="flex items-center justify-between"
                                  key={sz.id}
                                >
                                  <span>{sz.name}</span>
                                  <button
                                    className="ml-2 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                                    onClick={() => {
                                      setEditSubzoneData(sz);
                                      setShowEditSubzone(true);
                                    }}
                                  >
                                    Edit
                                  </button>
                                </li>
                              ))
                          )}
                        </ul>
                      </td>
                      <td className="px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <button
                          className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                          onClick={() => {
                            setEditZoneData(zone);
                            setShowEditZone(true);
                          }}
                        >
                          Edit Zone
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === "deliveries" && (
        <>
          {showAddDelivery && (
            <AddDeliveryModal
              drivers={drivers}
              onClose={() => setShowAddDelivery(false)}
              onSubmit={async (data) => {
                try {
                  await CourierService.createDelivery({
                    assigned_driver: data.assignedDriver,
                    customer_id: user?.id,
                    delivery_date: data.deliveryDate,
                    delivery_option: data.deliverySpeed,
                    delivery_time: data.deliveryTime,
                    items_count: data.itemsCount,
                    location: data.location,
                    notes: data.notes,
                    parcel_type: data.parcelType,
                    parcel_weight: data.weight,
                    pickup_date: data.pickupDate,
                    receiver_address: data.receiverAddress,
                    receiver_identity: data.receiverIdPassport,
                    receiver_name: data.receiverName,
                    receiver_phone: data.receiverPhone,
                    sender_identity: data.senderIdPassport,
                    sender_name: data.senderName,
                    sender_phone: data.senderPhone,
                    subzone: data.subzone,
                    zone: data.zone,
                  });
                  toast.success("Delivery added successfully");
                  setShowAddDelivery(false);
                  await fetchDeliveries();
                } catch (err) {
                  let errorMsg = "Failed to add delivery";
                  if (err && typeof err === "object" && "response" in err) {
                    const errorWithResponse = err as any;
                    if (errorWithResponse.response?.data?.errors) {
                      errorMsg +=
                        ": " +
                        Object.values(
                          errorWithResponse.response.data.errors,
                        ).join(", ");
                    }
                  }
                  toast.error(errorMsg);
                  console.error("Add delivery error:", err);
                }
              }}
              open={showAddDelivery}
              user={
                user
                  ? {
                      address: user.address || "",
                      name: user.name || user.full_name || "",
                      phone: user.phone || "",
                    }
                  : undefined
              }
            />
          )}
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Deliveries ({filteredDeliveries.length})
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                View:
              </span>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${viewType["deliveries"] === "card" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() =>
                  setViewType((prev) => ({ ...prev, deliveries: "card" }))
                }
              >
                Grid
              </button>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${viewType["deliveries"] === "table" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() =>
                  setViewType((prev) => ({ ...prev, deliveries: "table" }))
                }
              >
                Table
              </button>
              <button
                className="ml-2 flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 sm:ml-4 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
                onClick={() => setShowAddDelivery(true)}
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Add Delivery</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>
          {/* Filters, Search & Export */}
          <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <select
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:w-auto sm:text-sm"
              onChange={(e) => setDeliveryStatusFilter(e.target.value)}
              value={deliveryStatusFilter}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="picked_up">Picked Up</option>
              <option value="in_transit">In Transit</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <input
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:flex-1 sm:text-sm"
              onChange={(e) => setDeliverySearch(e.target.value)}
              placeholder="Search deliveries..."
              type="text"
              value={deliverySearch}
            />
            <button
              className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700 sm:text-sm"
              onClick={() => {
                const csv = [
                  ["ID", "Type", "Status", "Courier", "Zone", "Created"],
                  ...filteredDeliveries.map((d: any) => [
                    d.id,
                    d.type || d.parcel_type || "",
                    d.status,
                    d.courier_name || d.courier?.name || d.courier_id || "",
                    d.zone || d.zone_name || "",
                    d.created_at ? new Date(d.created_at).toLocaleString() : "",
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "deliveries.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>
          {loadingDeliveries ? (
            <div className="text-gray-500 dark:text-gray-400">
              Loading deliveries...
            </div>
          ) : filteredDeliveries.length === 0 ? (
            <div className="text-gray-500 dark:text-gray-400">
              No deliveries found.
            </div>
          ) : viewType["deliveries"] === "card" ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredDeliveries.map((delivery: any) => (
                <div
                  className="flex flex-col gap-2 rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                  key={delivery.id}
                >
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      {delivery.type || delivery.parcel_type || ""}
                    </h4>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <span>Status:</span>
                      <select
                        className="rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          try {
                            await CourierService.updateDeliveryStatus(
                              delivery.id,
                              newStatus,
                            );
                            toast.success("Status updated successfully!");
                            await fetchDeliveries();
                          } catch (err) {
                            toast.error("Failed to update status");
                            console.error("Update status error:", err);
                          }
                        }}
                        value={delivery.status}
                      >
                        <option value="pending">Pending</option>
                        <option value="picked_up">Picked Up</option>
                        <option value="in_transit">In Transit</option>
                        <option value="out_for_delivery">
                          Out for Delivery
                        </option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Destination:{" "}
                      {(() => {
                        if (delivery.subzone) {
                          // Try filteredZones first, then subzones
                          const subzoneObj = subzones.find(
                            (sz: any) =>
                              String(sz.id) === String(delivery.subzone),
                          );
                          return (
                            subzoneObj?.name ||
                            subzoneObj?.subzone_name ||
                            delivery.subzone
                          );
                        }
                        return (
                          delivery.receiver_address ||
                          delivery.receiverAddress ||
                          ""
                        );
                      })()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Zone:{" "}
                      {(() => {
                        if (delivery.zone) {
                          const zoneObj = filteredZones?.find?.(
                            (z: any) => String(z.id) === String(delivery.zone),
                          );
                          return (
                            zoneObj?.name || zoneObj?.zone_name || delivery.zone
                          );
                        }
                        return (
                          delivery.location ||
                          delivery.zone ||
                          delivery.zone_name ||
                          ""
                        );
                      })()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Created:{" "}
                      {delivery.created_at
                        ? new Date(delivery.created_at).toLocaleString()
                        : ""}
                    </p>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => {
                        setEditDeliveryData(delivery);
                        setShowEditDelivery(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      className="text-red-600 hover:text-red-800"
                      onClick={() => {
                        /* setDeliveryToDelete(delivery); setShowDeleteDialog(true); */
                      }}
                    >
                      {/* Delete button removed */}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs dark:divide-gray-700 sm:text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      ID
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Destination
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Zone
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Created
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                  {filteredDeliveries.map((delivery: any) => (
                    <tr key={delivery.id}>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {delivery.id}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {delivery.type || delivery.parcel_type || ""}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <select
                          className="rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            try {
                              await CourierService.updateDeliveryStatus(
                                delivery.id,
                                newStatus,
                              );
                              toast.success("Status updated successfully!");
                              await fetchDeliveries();
                            } catch (err) {
                              toast.error("Failed to update status");
                              console.error("Update status error:", err);
                            }
                          }}
                          value={delivery.status}
                        >
                          <option value="pending">Pending</option>
                          <option value="picked_up">Picked Up</option>
                          <option value="in_transit">In Transit</option>
                          <option value="out_for_delivery">
                            Out for Delivery
                          </option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {(() => {
                          if (delivery.subzone) {
                            const subzoneObj = subzones.find(
                              (sz: any) =>
                                String(sz.id) === String(delivery.subzone),
                            );
                            return (
                              subzoneObj?.name ||
                              subzoneObj?.subzone_name ||
                              delivery.subzone
                            );
                          }
                          return (
                            delivery.receiver_address ||
                            delivery.receiverAddress ||
                            ""
                          );
                        })()}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {(() => {
                          if (delivery.zone) {
                            const zoneObj = filteredZones?.find?.(
                              (z: any) =>
                                String(z.id) === String(delivery.zone),
                            );
                            return (
                              zoneObj?.name ||
                              zoneObj?.zone_name ||
                              delivery.zone
                            );
                          }
                          return (
                            delivery.location ||
                            delivery.zone ||
                            delivery.zone_name ||
                            ""
                          );
                        })()}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        {delivery.created_at
                          ? new Date(delivery.created_at).toLocaleString()
                          : ""}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                        <button
                          className="mr-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                          onClick={() => {
                            setEditDeliveryData(delivery);
                            setShowEditDelivery(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                          onClick={() => {
                            /* setDeliveryToDelete(delivery); setShowDeleteDialog(true); */
                          }}
                        >
                          {/* Delete button removed */}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {showEditDelivery && editDeliveryData && (
            <>
              {console.log(
                "Edit modal initialData weight:",
                editDeliveryData.weight,
                typeof editDeliveryData.weight,
              )}
              <EditDeliveryModal
                drivers={drivers}
                initialData={{
                  deliveryDate:
                    editDeliveryData.deliveryDate ||
                    editDeliveryData.delivery_date ||
                    "",
                  deliverySpeed:
                    editDeliveryData.deliverySpeed ||
                    editDeliveryData.delivery_option ||
                    "",
                  notes: editDeliveryData.notes || "",
                  parcelType:
                    editDeliveryData.parcelType ||
                    editDeliveryData.parcel_type ||
                    "",
                  pickupDate:
                    editDeliveryData.pickupDate ||
                    editDeliveryData.pickup_date ||
                    "",
                  receiverAddress:
                    editDeliveryData.receiverAddress ||
                    editDeliveryData.receiver_address ||
                    "",
                  receiverName:
                    editDeliveryData.receiverName ||
                    editDeliveryData.receiver_name ||
                    "",
                  receiverPhone:
                    editDeliveryData.receiverPhone ||
                    editDeliveryData.receiver_phone ||
                    "",
                  senderAddress:
                    editDeliveryData.senderAddress ||
                    editDeliveryData.sender_address ||
                    "",
                  senderName:
                    editDeliveryData.senderName ||
                    editDeliveryData.sender_name ||
                    "",
                  senderPhone:
                    editDeliveryData.senderPhone ||
                    editDeliveryData.sender_phone ||
                    "",
                  subzone: editDeliveryData.subzone || "",
                  weight: Number(
                    editDeliveryData.weight ??
                      editDeliveryData.parcel_weight ??
                      0,
                  ),
                  zone: editDeliveryData.zone || "",
                }}
                onClose={() => setShowEditDelivery(false)}
                onSubmit={async (data) => {
                  // Debug: log weight before sending
                  console.log(
                    "Submitting weight:",
                    data.weight,
                    typeof data.weight,
                  );
                  await CourierService.updateDelivery(editDeliveryData.id, {
                    delivery_date: data.deliveryDate,
                    delivery_option: data.deliverySpeed,
                    notes: data.notes,
                    parcel_type: data.parcelType,
                    parcel_weight: Number(data.weight),
                    pickup_date: data.pickupDate,
                    receiver_address: data.receiverAddress,
                    receiver_identity: data.receiverAddress,
                    receiver_name: data.receiverName,
                    receiver_phone: data.receiverPhone,
                    sender_identity: data.senderAddress, // sender_identity from senderAddress
                    sender_name: data.senderName,
                    sender_phone: data.senderPhone,
                    subzone: data.subzone,
                    zone: data.zone,
                  });
                  toast.success("Delivery updated successfully");
                  setShowEditDelivery(false);
                  await fetchDeliveries();
                }}
                open={showEditDelivery}
                subzones={subzones}
                user={
                  user
                    ? {
                        address: user.address || "",
                        name: user.name || user.full_name || "",
                        phone: user.phone || "",
                      }
                    : undefined
                }
                zones={zones}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CourierManagement;
